import React, { ReactElement, lazy, Suspense } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import {
  HOMEPAGE_PATH,
  PARKS_PATH,
  BOOK_PATH,
  BOOK_COTTAGE_PATH,
  BOOK_MAINTENANCE_PATH,
  RESERVATIONS_PATH,
  LOGIN_PATH,
  SIGNOUT_PATH,
  REGISTER_PATH,
  PROFILE_PATH,
  RESERVATION_DETAIL_PATH,
  RESERVATIONS_EXTENSION_PATH,
  CONFIRMATION_PATH,
  PROFILE_EDIT_PATH,
  AUTH_PATH,
  FINNISH_REGISTER_PATH,
  PAYMENT_REDIRECT_PATH,
  SERVICE_ENTRY_PATH,
  SERVICE_PAYMENT_REDIRECT_PATH,
  SERVICE_PAYMENT_CONFIRMATION_PATH,
  SERVICE_ENTRY_DETAILS,
} from 'constants/paths';
import { VALID_LOCALES, DEFAULT_LOCALE, FINNISH } from 'constants/locale';
import PageLoader from 'components/page-loader/page-loader';
import Confirmation from 'scenes/confirmation/confirmation';
import { ReservationsProvider } from 'contexts/reservations-context';
import { geoApi } from 'api/bestpark/geolocation';
import { useQuery } from 'react-query';
import { getLanguageCodeByCountryCode } from 'helpers/language-helpers';
import { getLocaleNoDefault } from 'helpers/path-helpers';

import PrivateRoute from './private-route';
import ErrorPage from 'scenes/error-page/error-page';
import { LOADING } from 'constants/query';
import { ServiceEntryProvider } from 'contexts/service-entry-context';

const AuthLayout = lazy(async () => import('scenes/auth-page/auth-page'));
const ReservationLayout = lazy(async () =>
  import('layouts/reservation-layout/reservation-layout'),
);
const ServiceEntryLayout = lazy(async () =>
  import('layouts/service-entry-layout/service-entry-layout'),
);
const LoginPage = lazy(async () => import('scenes/login-page/login-page'));
const SignoutRedirect = lazy(async () => import('scenes/signout/signout'));
const RegisterPage = lazy(async () =>
  import('scenes/register-page/register-page'),
);
const PaymentRedirect = lazy(async () =>
  import('scenes/payment-redirect/payment-redirect'),
);
const ServiceEntryPaymentRedirect = lazy(async () =>
  import(
    'scenes/service-entry/service-entry-payment-redirect/service-entry-payment-redirect'
  ),
);
const ServiceEntryConfirmation = lazy(async () =>
  import(
    'scenes/service-entry/service-entry-confirmation/service-entry-confirmation'
  ),
);
const ServiceEntryDetails = lazy(async () =>
  import('scenes/service-entry/service-entry-details/service-entry-details'),
);
const Book = lazy(async () => import('scenes/book/book'));
const BookCottage = lazy(async () =>
  import('scenes/book-cottage/book-cottage'),
);
const ParkRedirect = lazy(async () =>
  import('scenes/parks-list/park-redirect'),
);
const ProfilePage = lazy(async () => import('scenes/profile/profile'));
const ReservationDetails = lazy(async () =>
  import('scenes/reservation/details/details'),
);
const ReservationExtension = lazy(async () =>
  import('scenes/reservation/extension/extension'),
);
const ProfileEditPage = lazy(async () =>
  import('scenes/profile-edit-page/profile-edit-page'),
);

const CustomError = (): null => {
  window.location.href = '/error/404.html';
  return null;
};

export default function AppRoutes(): ReactElement {
  const { status, data } = useQuery(['userLang'], geoApi.getGeolocationData);

  const { pathname } = window.location;
  if (status === LOADING && pathname !== HOMEPAGE_PATH) return <PageLoader />;

  const userLang = getLanguageCodeByCountryCode(data?.country);
  const base = `/:locale(${VALID_LOCALES.join('|')})?`;
  const pathLocale = getLocaleNoDefault(pathname);

  // If pathname does not start with any locale, append it unless the geo
  // locale is the same as the default locale.
  if (!VALID_LOCALES.includes(pathLocale) && userLang !== DEFAULT_LOCALE) {
    window.location.href = `${window.origin}/${userLang}${window.location.pathname}${window.location.search}`;
  }
  // Temply disable as buggy
  // else if (pathLocale === DEFAULT_LOCALE) {
  //   // When path has a locale but the same as the default locale, remove
  //   // the locale from the pathname.
  //   redirectTo = window.location.pathname.replace(`/${pathLocale}`, '');
  //   console.log('redirect to value', redirectTo);
  //   redirectTo = redirectTo !== '' ? redirectTo : '/';
  // }

  return (
    <ReservationsProvider>
      <ServiceEntryProvider>
        <Suspense fallback={<PageLoader />}>
          <Switch>
            <Route
              exact
              path={base}
              component={(): null => {
                window.location.replace(
                  process.env.REACT_APP_BESTPARK_WP ?? LOGIN_PATH,
                );
                return null;
              }}
            />
            <Route path={`${base}${AUTH_PATH}`} component={AuthLayout} />
            <Route
              path={`${base}${RESERVATIONS_PATH}`}
              component={ReservationLayout}
            />
            {/* service entry redirect, details, and confirmation should come first before service entry layout */}
            <PrivateRoute
              path={`${base}${SERVICE_ENTRY_DETAILS}/:id`}
              component={ServiceEntryDetails}
            />
            <Route
              path={`${base}${SERVICE_PAYMENT_REDIRECT_PATH}/:id`}
              component={ServiceEntryPaymentRedirect}
            />
            <Route
              path={`${base}${SERVICE_PAYMENT_CONFIRMATION_PATH}/:id`}
              component={ServiceEntryConfirmation}
            />
            <Route
              path={`${base}${SERVICE_ENTRY_PATH}`}
              component={ServiceEntryLayout}
            />
            <Route
              exact
              path={`${base}${PARKS_PATH}/:id`}
              component={ParkRedirect}
            />
            <Route exact path={`${base}${BOOK_PATH}/:id`} component={Book} />
            <Route
              exact
              path={`${base}${BOOK_COTTAGE_PATH}/:id/:categoryid`}
              component={BookCottage}
            />
            <Route
              exact
              path={`${base}${BOOK_PATH}/:id/:categoryid`}
              component={BookCottage}
            />
            <Route
              exact
              path={`${base}${BOOK_MAINTENANCE_PATH}/:id`}
              component={ServiceEntryLayout}
            />
            <Route path={`${base}${LOGIN_PATH}`} component={LoginPage} />
            <Route
              path={`${base}${SIGNOUT_PATH}`}
              component={SignoutRedirect}
            />
            <Route path={`${base}${REGISTER_PATH}`} component={RegisterPage} />
            <Route
              path={`${base}${PAYMENT_REDIRECT_PATH}/:id`}
              component={PaymentRedirect}
            />
            <Route
              path={`${base}${FINNISH_REGISTER_PATH}`}
              component={() => <Redirect to={`/${FINNISH}${REGISTER_PATH}`} />}
            />
            <PrivateRoute
              path={`${base}${PROFILE_EDIT_PATH}`}
              component={ProfileEditPage}
            />
            <PrivateRoute
              path={`${base}${PROFILE_PATH}`}
              component={ProfilePage}
            />
            <PrivateRoute
              path={`${base}${RESERVATION_DETAIL_PATH}/:id`}
              component={ReservationDetails}
            />
            <PrivateRoute
              path={`${base}${RESERVATIONS_EXTENSION_PATH}/:id`}
              component={ReservationExtension}
            />
            <Route
              path={`${base}${CONFIRMATION_PATH}/:id`}
              component={Confirmation}
            />
            <Route
              component={
                process.env.NODE_ENV !== 'development' ? CustomError : ErrorPage
              }
            />
          </Switch>
        </Suspense>
      </ServiceEntryProvider>
    </ReservationsProvider>
  );
}
